import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  createActivity,
  deleteActivity,
  listActivities,
  listMilestones,
  listProgress,
  recordProgress,
  unwaiveActivity,
  updateActivity,
  waiveActivity,
  type Activity,
  type ActivityStatus,
  type Milestone,
  type ProgressRecord,
} from '../api.js';
import { formatDate } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { focusClass, useFocusRow } from './cross-links.js';
import { Dependencies } from './Dependencies.js';
import { recordHref, sectionHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * The work on site, and how far along it is.
 *
 * TWO THINGS THIS SCREEN WILL NOT LET YOU DO
 * ------------------------------------------
 * **Type over the progress figure.** There is no percent field on the edit
 * form. Progress is entered through "Record progress", which keeps the date,
 * the change and who said so — because the figure is what a monthly valuation
 * is argued over, and "who said 60%, and what was it before" is the question
 * that gets asked months later by somebody who was not there.
 *
 * **Waive something in passing.** Waiving is what lets the Site Execution stage
 * complete over unfinished work, so it takes a written reason and keeps it
 * visible on the row afterwards — the same shape as a stage override.
 *
 * ON THE REGISTER PATTERN — and why the two-week box is not a filter
 * -----------------------------------------------------------------
 * "Next two weeks only" is a SERVER-side window: it changes what is fetched,
 * not what is shown of it. Putting it in the toolbar beside the client-side
 * filters would make the count line say "showing 8 of 8" while eighty
 * activities sat unfetched behind it, which is exactly the kind of quiet
 * hiding the toolbar exists to stop. So it keeps its own box, above, with the
 * sentence that matters most about it: late work stays in the list however old
 * it is.
 *
 * The row's one action is **Record progress** — the thing a site engineer does
 * every day, on every row. Waiving and removing stay in the overflow menu.
 */

const STATUS_LABELS: Readonly<Record<ActivityStatus, string>> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
  waived: 'Waived',
};

const STATUS_SYMBOLS: Readonly<Record<ActivityStatus, string>> = {
  not_started: '○',
  in_progress: '●',
  complete: '✓',
  waived: '—',
};

const MIN_REASON = 20;

interface Props {
  readonly projectId: string;
  /**
   * Decided by the server. `04` gives a Site Engineer `VCE` — they record
   * progress against their own work — while waiving needs approval rights. A
   * button that answers 403 is a worse screen than no button.
   */
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    readonly waive: boolean;
    /** `activity: X`. Gates the button; the rows are already filtered. */
    readonly exportRows: boolean;
  };
  /**
   * One activity, as its own page, from
   * `/projects/<id>/site/activities/<recordId>`.
   *
   * An activity EARNS a page: its progress history is a list of who said what
   * percentage and when, the photos behind those claims are another, and what
   * the work waits on is a third. "60% done" is only worth anything beside the
   * record of who said so and the photograph they said it about, and a drawer
   * stacks those three so that reading one means scrolling the others away.
   */
  readonly recordId?: string | null;
}

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` is gone: the activity has a page and an address now (§4.3). The forms
 * stay, `record` and `waive` above all — a percentage goes onto a permanent
 * history, and waiving takes an activity out of the Site gate and demands a
 * written reason for it.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'record'; readonly id: string }
  | { readonly kind: 'waive'; readonly id: string }
  | { readonly kind: 'add' };

export function Activities({ projectId, recordId = null, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Activity[] | null>(null);
  /**
   * How many activities survive the lookahead and the scope on the server,
   * against how many are loaded here. See `api/paging.ts`.
   */
  const [registerTotal, setRegisterTotal] = useState(0);
  const [wantAll, setWantAll] = useState(false);
  /** Not the same as an empty register. The Day 17 bug, guarded from the start. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  const [milestones, setMilestones] = useState<readonly Milestone[]>([]);
  const view = useRegisterView('planned');
  /**
   * `03` ST-02 makes the two-week lookahead the default view. It is what a site
   * engineer opens the screen to see; the whole programme is a click away.
   *
   * **OFF ON A RECORD PAGE, and that is load-bearing rather than tidy.** The
   * lookahead is a SERVER WINDOW (§4.1c), not a client-side filter, so a page
   * left on the default would ask for the next fortnight, fail to find an
   * activity planned for March, and say the activity did not exist — which is
   * the `?focus=` failure the record page exists to fix, reproduced exactly.
   * Tasks has the same trap and the same answer; see its filter state.
   */
  const [lookahead, setLookahead] = useState(recordId === null);

  const load = useCallback(
    async (window: boolean) => {
      try {
        const page = await listActivities(
          projectId,
          { lookahead: window },
          wantAll ? LOAD_ALL_LIMIT : undefined,
        );
        setItems(page.rows);
        setRegisterTotal(page.total);
        setDenied(false);
      } catch (caught) {
        // 404 counts as refused: a non-member gets 404 rather than 403 so the
        // project's existence is not confirmed (ADR-004).
        const refused =
          caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
        setDenied(refused);
        setError(refused ? null : 'Could not load the activities.');
        setItems([]);
      }
      // The milestones an activity can be declared to deliver (`06`'s
      // `activity → milestone: delivers_to`). Silent on failure: a Site
      // Engineer holds `milestone: V` and might not; they lose the dropdown,
      // not the register.
      try {
        setMilestones(await listMilestones(projectId));
      } catch {
        setMilestones([]);
      }
    },
    [projectId, wantAll],
  );

  useEffect(() => {
    void load(lookahead);
  }, [load, lookahead]);

  async function run(work: () => Promise<readonly Activity[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await work();
      // Re-read through the current filter: a task that just reached 100% drops
      // out of the lookahead, and showing the unfiltered answer back would put
      // it in a list it no longer belongs to.
      await load(lookahead);
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!items) return [];

    const stage = view.chosen.stage;

    const filtered = items.filter((a) => {
      // Everything somebody would type: what the work is, the trade doing it,
      // and where on the job it is.
      const hay = [a.name, a.trade_name, a.location_path].filter(Boolean).join(' ');
      if (!matches(hay, view.query)) return false;
      if (stage === 'late' && !(a.days_late > 0)) return false;
      if (stage && stage !== 'late' && a.status !== stage) return false;
      return true;
    });

    // A null date sorts LAST, never first: work nobody has dated is not the
    // most urgent thing on site.
    const byPlanned = (a: Activity, b: Activity): number => {
      const x = a.planned_finish;
      const y = b.planned_finish;
      if (x === y) return 0;
      if (!x) return 1;
      if (!y) return -1;
      return x < y ? -1 : 1;
    };

    const compare: Readonly<Record<string, (a: Activity, b: Activity) => number>> = {
      planned: byPlanned,
      late: (a, b) => b.days_late - a.days_late || byPlanned(a, b),
      // Least done first — the ones with the most work left in them.
      done: (a, b) => Number(a.percent_complete) - Number(b.percent_complete),
      name: (a, b) => a.name.localeCompare(b.name),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.planned!);
  }, [items, view.query, view.chosen.stage, view.sortKey]);

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add' ? (items?.find((a) => a.id === drawer.id) ?? null) : null;

  /**
   * The one move that goes on the row.
   *
   * Recording progress is what somebody repeats down this register — it is the
   * whole reason a site engineer opens the screen. A waived activity has no
   * progress to record, so its row opens the record instead.
   */
  function primaryFor(item: Activity): RowAction {
    if (can.edit && item.status !== 'waived') {
      return {
        label: 'Record progress',
        disabled: busy,
        onSelect: () => setDrawer({ kind: 'record', id: item.id }),
      };
    }
    return {
      label: 'Open',
      onSelect: () => navigate(recordHref(projectId, 'activities', item.id)),
    };
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page. Two entries differ, and
   * both for the same reason: on the page you are already looking at the record.
   */
  function overflowFor(
    item: Activity,
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = onPage
      ? []
      : [
          {
            label: 'Open',
            onSelect: () => navigate(recordHref(projectId, 'activities', item.id)),
          },
        ];
    if (can.edit) {
      actions.push({ label: 'Edit', onSelect: () => setDrawer({ kind: 'edit', id: item.id }) });
    }
    if (can.waive) {
      if (item.status === 'waived') {
        actions.push({
          label: 'Un-waive',
          disabled: busy,
          onSelect: () => void run(() => unwaiveActivity(projectId, item.id)),
        });
      } else if (item.status !== 'complete') {
        actions.push({
          label: 'Waive',
          danger: true,
          disabled: busy,
          onSelect: () => setDrawer({ kind: 'waive', id: item.id }),
        });
      }
    }
    if (can.remove) {
      actions.push({
        label: 'Remove',
        danger: true,
        disabled: busy,
        onSelect: () => {
          void run(() => deleteActivity(projectId, item.id)).then((ok) => {
            // Removing the record you are standing on leaves you on an address
            // that names nothing. The register is where the removal makes sense.
            if (ok && onPage) navigate(sectionHref(projectId, 'site'));
          });
        },
      });
    }
    // Whatever is already on the row must not also be in the menu behind it.
    return primary ? actions.filter((action) => action.label !== primary.label) : actions;
  }

  async function exportRows(): Promise<void> {
    const headers = [
      'Activity',
      'Trade',
      'Where',
      'Planned finish',
      'Done (%)',
      'Status',
      'Days late',
      'Waived because',
    ];
    const rows = shown.map((a) => [
      a.name,
      a.trade_name ?? '',
      a.location_path ?? '',
      a.planned_finish ?? '',
      String(Number(a.percent_complete)),
      STATUS_LABELS[a.status],
      a.days_late > 0 ? String(a.days_late) : '',
      a.waiver_reason ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'activity',
        filename: `activities-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Activities</h3>
      {items && can.write && (
        <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
          Add activity
        </button>
      )}
    </div>
  );

  /*
   * The same four states on a record page, still four different sentences. The
   * furniture above them changes: "Add activity" is the wrong offer to somebody
   * who followed a link to one particular activity.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="activities" /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s activities.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {above}
        <p aria-live="polite">Loading {recordId ? 'the activity' : 'activities'}…</p>
      </>
    );
  }

  /**
   * The four forms, shared by the register and the record page.
   *
   * `RecordForm` and `WaiveForm` above all: a percentage goes onto a permanent
   * progress history, and waiving takes an activity out of the Site gate and
   * demands a written reason for doing it. A page with its own copy of either is
   * a second place for those rules to drift.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Add an activity" dirty={formDirty} onClose={closeDrawer}>
          <AddForm
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createActivity(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer title={`Edit ${subject.name}`} dirty={formDirty} onClose={closeDrawer}>
          <EditForm
            item={subject}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => updateActivity(projectId, subject.id, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'record' && subject && (
        <Drawer
          title={`Record progress — ${subject.name}`}
          subtitle={`Now at ${Number(subject.percent_complete)}%`}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <RecordForm
            item={subject}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => recordProgress(projectId, subject.id, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'waive' && subject && (
        <Drawer title={`Waive ${subject.name}`} dirty={formDirty} onClose={closeDrawer}>
          <WaiveForm
            item={subject}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (reason) => {
              if (await run(() => waiveActivity(projectId, subject.id, reason))) closeDrawer();
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ------------------------------------------------------ one activity, in full
  if (recordId) {
    const record = items.find((a) => a.id === recordId) ?? null;
    if (!record) {
      /* A FAILED LOAD IS NOT A MISSING RECORD — see `Materials.tsx`. */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="activities" />
      );
    }

    const forward = primaryFor(record);
    const primary = forward.label === 'Open' ? null : forward;
    /* NO BAR WHEN THERE IS NOTHING IN IT — see `Drawings.tsx`. */
    const more = overflowFor(record, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={record.name} {...(primary ? { primary } : {})} more={more} />
      ) : null;

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="activities"
          title={record.name}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              <span className="mono">{Number(record.percent_complete)}% done</span>
              {record.trade_name && <> · {record.trade_name}</>}
              {record.location_path && <> · {record.location_path}</>}
              {/* Lateness in words, never colour alone, and visible at the top
                  for the same reason it stays on the row at both densities. */}
              {record.days_late > 0 && (
                <span className="task-overdue">
                  {' · '}
                  {record.days_late} {record.days_late === 1 ? 'day' : 'days'} late
                </span>
              )}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<ActivityProperties item={record} />}
        >
          {/*
            THE PROGRESS HISTORY AND THE PHOTOS SIT TOGETHER, and that is the
            argument for this record having a page. "60% done" is worth nothing
            on its own; it is worth something beside the record of who said so
            and the photograph they said it about. In a drawer, reading one meant
            scrolling the other away.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Progress recorded</h4>
            </div>
            {record.progress_count > 0 ? (
              <History projectId={projectId} activityId={record.id} />
            ) : (
              <p className="hint">
                No progress has been recorded against this yet. Progress rolls up from these, so
                the project percentage is understated until somebody records some.
              </p>
            )}
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Progress photos</h4>
            </div>
            {/*
              The photo behind "this is 60% done". Shared by default: progress is
              exactly what a client asks to see.
            */}
            <Attachments
              projectId={projectId}
              entityType="activity"
              entityId={record.id}
              can={{ write: can.edit }}
              noun="progress photos"
              shareByDefault
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">What it waits on</h4>
            </div>
            {/*
              `06` P4 — the same one dependency model tasks use, not a second
              one.
            */}
            <Dependencies
              projectId={projectId}
              entityType="activity"
              entityId={record.id}
              candidates={items.map((a) => ({ id: a.id, label: a.name }))}
              milestones={milestones.map((m) => ({ id: m.id, label: m.name }))}
              canEdit={can.edit}
              onChanged={() => void load(lookahead)}
            />
          </div>
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {/* Not a toolbar filter — see the note at the top of the file. */}
      <div className="filter-row">
        <label className="check-row">
          <input
            type="checkbox"
            checked={lookahead}
            disabled={busy}
            onChange={(e) => setLookahead(e.target.checked)}
          />
          <span>
            Next two weeks only
            <span className="hint"> — late work stays in this list however old it is.</span>
          </span>
        </label>
      </div>

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>
              {lookahead ? 'Nothing due in the next two weeks.' : 'No activities yet.'}
            </strong>
          </p>
          <p>
            {lookahead
              ? 'Nothing is late either. Untick the box above to see the whole programme.'
              : can.write
                ? 'These are the pieces of work on site. Add them, then record progress against each — the Site Execution stage needs every one at 100% or waived before it can be completed.'
                : /*
                   * An empty state must name an action ITS READER can take. A
                   * QS or a client holds `activity: V` and no create.
                   */
                  'These are the pieces of work on site. Site or the Project Manager lists them, and the Site Execution stage needs every one at 100% or waived before it can be completed.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search activities"
            searchPlaceholder="What it is, the trade, where"
            shown={shown.length}
            total={items.length}
            registerTotal={registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && items.length < registerTotal}
            noun={['activity', 'activities']}
            filters={[
              {
                key: 'stage',
                label: 'Show',
                options: [
                  { value: 'late', label: 'Late' },
                  { value: 'not_started', label: 'Not started' },
                  { value: 'in_progress', label: 'In progress' },
                  { value: 'complete', label: 'Complete' },
                  { value: 'waived', label: 'Waived' },
                ],
              },
            ]}
            sorts={[
              { key: 'planned', label: 'Planned finish' },
              { key: 'late', label: 'Most late' },
              { key: 'done', label: 'Least done' },
              { key: 'name', label: 'Name' },
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === items.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "nothing due". This reader has a
             * list in front of them and a filter that excludes all of it.
             */
            <div className="empty">
              <p>
                <strong>No activity matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {items.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  Activities on this project, with where they are, how far along, and what is late.{' '}
                  {shown.length} of {items.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Activity</th>
                    <th scope="col">Where</th>
                    <th scope="col">Planned</th>
                    <th scope="col">Done</th>
                    <th scope="col">Status</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((item) => {
                    const primary = primaryFor(item);
                    return (
                      <ActivityRow key={item.id} item={item}>
                        <td>
                          {/*
                            A REAL LINK, because the record has an address now —
                            so a middle-click, a new tab and a copied link all
                            mean what they look like they mean.
                          */}
                          <AppLink
                            className="rk-open"
                            to={recordHref(projectId, 'activities', item.id)}
                          >
                            {item.name}
                            <span className="visually-hidden"> — open this activity</span>
                          </AppLink>
                          <span className="rk-secondary">
                            {item.trade_name && (
                              <span className="tree-code"> · {item.trade_name}</span>
                            )}
                            {/*
                              The waiver reason stays on the row for good, like
                              a stage override. An activity that closed a gate
                              over unfinished work must never look like one that
                              was actually finished.
                            */}
                            {item.status === 'waived' && item.waiver_reason && (
                              <span className="snag-note">
                                Waived: {item.waiver_reason}
                                {item.waived_by_name && ` — ${item.waived_by_name}`}
                              </span>
                            )}
                          </span>
                        </td>
                        <td data-label="Where">{item.location_path ?? '—'}</td>
                        <td data-label="Planned">
                          {item.planned_finish ? formatDate(item.planned_finish) : '—'}
                          {/*
                            Late IN WORDS, not by the red row alone. `10`:
                            status is never carried by colour on its own, and
                            this is the column somebody scans to find what has
                            slipped. It stays at both densities.
                          */}
                          {item.days_late > 0 && (
                            <span className="task-overdue">
                              {' '}
                              {item.days_late} {item.days_late === 1 ? 'day' : 'days'} late
                            </span>
                          )}
                        </td>
                        <td data-label="Done" className="mono">
                          {Number(item.percent_complete)}%
                        </td>
                        <td data-label="Status">
                          <span className="stage-symbol" aria-hidden="true">
                            {STATUS_SYMBOLS[item.status]}
                          </span>{' '}
                          {STATUS_LABELS[item.status]}
                        </td>
                        <td>
                          <RowActions
                            rowLabel={item.name}
                            primary={primary}
                            more={overflowFor(item, primary)}
                          />
                        </td>
                      </ActivityRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/** One row, which knows whether a cross-link came looking for it. */
function ActivityRow({
  item,
  children,
}: {
  readonly item: Activity;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(item.id);
  return (
    <tr
      ref={ref}
      aria-current={focused ? 'true' : undefined}
      className={focusClass(
        focused,
        `activity act-${item.status}${item.days_late > 0 ? ' is-late' : ''}`,
      )}
    >
      {children}
    </tr>
  );
}

/**
 * What the activity IS — the record page's rail.
 *
 * The three LISTS — the progress history, the photographs behind it and what the
 * work waits on — are in the main column. All three used to open as extra rows
 * inside the table, which pushed every activity below them down the page; the
 * drawer stacked them; the page puts the history and the evidence for it beside
 * each other, which is the only arrangement where "60% done" and the photograph
 * somebody said it about can be read together.
 */
function ActivityProperties({ item }: { readonly item: Activity }): JSX.Element {
  return (
    <>
      <dl className="detail-grid">
        <dt>Done</dt>
        <dd className="mono">{Number(item.percent_complete)}%</dd>
        <dt>Status</dt>
        <dd>
          {STATUS_LABELS[item.status]}
          {item.days_late > 0 && (
            <span className="task-overdue">
              {' '}
              · {item.days_late} {item.days_late === 1 ? 'day' : 'days'} late
            </span>
          )}
        </dd>
        <dt>Planned finish</dt>
        <dd>{formatDate(item.planned_finish)}</dd>
        <dt>Where</dt>
        <dd>{item.location_path ?? '—'}</dd>
        {item.trade_name && (
          <>
            <dt>Trade</dt>
            <dd>{item.trade_name}</dd>
          </>
        )}
        {item.status === 'waived' && item.waiver_reason && (
          <>
            <dt>Waived because</dt>
            <dd>
              {item.waiver_reason}
              {item.waived_by_name && ` — ${item.waived_by_name}`}
            </dd>
          </>
        )}
      </dl>
    </>
  );
}

// ---------------------------------------------------------------------------

function AddForm({
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    name: string;
    planned_start?: string | null;
    planned_finish?: string | null;
    budget_weight?: string;
  }) => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [finish, setFinish] = useState('');
  const [weight, setWeight] = useState('1');

  /*
   * Compared against the empty form rather than tracked with a touched flag:
   * somebody who types a character and deletes it has changed nothing. The
   * weight starts at 1, so it is compared against that.
   */
  useEffect(() => {
    onDirtyChange?.(name !== '' || start !== '' || finish !== '' || weight !== '1');
  }, [name, start, finish, weight, onDirtyChange]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    void onSubmit({
      name: name.trim(),
      planned_start: start || null,
      planned_finish: finish || null,
      budget_weight: weight || '1',
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field">
        <label htmlFor="act-name">What is the work?</label>
        <input
          id="act-name"
          value={name}
          maxLength={200}
          required
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="act-start">Planned start</label>
          <input id="act-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="act-finish">Planned finish</label>
          <input
            id="act-finish"
            type="date"
            value={finish}
            onChange={(e) => setFinish(e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="act-weight">Weight</label>
        <input
          id="act-weight"
          inputMode="decimal"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
        <p className="hint">
          How much of the job this represents. Leave it at 1 if everything counts the same —
          the project&rsquo;s overall progress is an average weighted by this.
        </p>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          Add
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function RecordForm({
  item,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly item: Activity;
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    percent_complete: string;
    entry_date?: string;
    note?: string | null;
  }) => void | Promise<void>;
}): JSX.Element {
  const [percent, setPercent] = useState(String(Number(item.percent_complete)));
  const [note, setNote] = useState('');
  const value = Number(percent);
  const valid = Number.isFinite(value) && value >= 0 && value <= 100;
  const delta = valid ? Math.round((value - Number(item.percent_complete)) * 100) / 100 : 0;

  // The percent box starts at the figure the activity already carries, so a
  // form nobody has touched is not dirty.
  useEffect(() => {
    onDirtyChange?.(percent !== String(Number(item.percent_complete)) || note !== '');
  }, [percent, note, item, onDirtyChange]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    void onSubmit({ percent_complete: percent, note: note.trim() || null });
  }

  return (
    <form className="panel" onSubmit={submit}>

      <div className="field">
        <label htmlFor={`pc-${item.id}`}>How far along is it now?</label>
        <input
          id={`pc-${item.id}`}
          inputMode="decimal"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
        />
        {/*
          The change said out loud BEFORE it is saved. Progress going backwards
          is legitimate — work gets re-measured down — but it should never be
          something somebody did without noticing.
        */}
        {valid && delta !== 0 && (
          <p className="hint">
            {delta > 0 ? `Up ${delta} points` : `Down ${Math.abs(delta)} points`} from{' '}
            {Number(item.percent_complete)}%.
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor={`note-${item.id}`}>Note (optional)</label>
        <textarea
          id={`note-${item.id}`}
          rows={2}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <p className="hint">
        This is kept permanently, with your name and today&rsquo;s date. It cannot be edited
        afterwards — a figure entered wrongly is corrected by recording the right one.
      </p>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !valid}>
          Record
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function EditForm({
  item,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly item: Activity;
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    name?: string;
    planned_start?: string | null;
    planned_finish?: string | null;
    forecast_finish?: string | null;
    budget_weight?: string;
  }) => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(item.name);
  const [start, setStart] = useState(item.planned_start ?? '');
  const [finish, setFinish] = useState(item.planned_finish ?? '');
  const [forecast, setForecast] = useState(item.forecast_finish ?? '');
  const [weight, setWeight] = useState(String(Number(item.budget_weight)));

  // Compared against the RECORD, so retyping a value back to what it already
  // was leaves the form clean.
  useEffect(() => {
    onDirtyChange?.(
      name !== item.name ||
        start !== (item.planned_start ?? '') ||
        finish !== (item.planned_finish ?? '') ||
        forecast !== (item.forecast_finish ?? '') ||
        weight !== String(Number(item.budget_weight)),
    );
  }, [name, start, finish, forecast, weight, item, onDirtyChange]);

  function submit(event: FormEvent): void {
    event.preventDefault();
    void onSubmit({
      name: name.trim(),
      planned_start: start || null,
      planned_finish: finish || null,
      forecast_finish: forecast || null,
      budget_weight: weight || '1',
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field">
        <label htmlFor={`name-${item.id}`}>Name</label>
        <input
          id={`name-${item.id}`}
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor={`ps-${item.id}`}>Planned start</label>
          <input
            id={`ps-${item.id}`}
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`pf-${item.id}`}>Planned finish</label>
          <input
            id={`pf-${item.id}`}
            type="date"
            value={finish}
            onChange={(e) => setFinish(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`ff-${item.id}`}>Expected finish</label>
          <input
            id={`ff-${item.id}`}
            type="date"
            value={forecast}
            onChange={(e) => setForecast(e.target.value)}
          />
        </div>
      </div>
      <p className="hint">
        The planned dates are what was programmed. The expected finish is what you believe now
        — move that one, and the planned pair keeps showing what was promised.
      </p>

      <div className="field">
        <label htmlFor={`bw-${item.id}`}>Weight</label>
        <input
          id={`bw-${item.id}`}
          inputMode="decimal"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
      </div>

      {/*
        NO progress field here, deliberately. It is on "Record progress", which
        keeps who said what and when.
      */}

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          Save
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function WaiveForm({
  item,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly item: Activity;
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (reason: string) => void | Promise<void>;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const enough = reason.trim().length >= MIN_REASON;

  useEffect(() => {
    onDirtyChange?.(reason !== '');
  }, [reason, onDirtyChange]);

  return (
    <form
      className="panel"
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(reason.trim());
      }}
    >
      <p className="hint">
        This says the project is no longer waiting for this work. It lets the Site Execution
        stage complete without it, so the reason is kept and shown on this row for good.
      </p>

      <div className="field">
        <label htmlFor={`wr-${item.id}`}>Why is the project not waiting for it?</label>
        <textarea
          id={`wr-${item.id}`}
          rows={3}
          maxLength={2000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters
        </p>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !enough}>
          Waive it
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

function History({
  projectId,
  activityId,
}: {
  readonly projectId: string;
  readonly activityId: string;
}): JSX.Element {
  const [rows, setRows] = useState<readonly ProgressRecord[] | null>(null);

  useEffect(() => {
    let live = true;
    void listProgress(projectId, activityId)
      .then((data) => {
        if (live) setRows(data);
      })
      .catch(() => {
        if (live) setRows([]);
      });
    return () => {
      live = false;
    };
  }, [projectId, activityId]);

  if (rows === null) return <p aria-live="polite">Loading the history…</p>;

  return (
    <ul className="task-list">
      {rows.map((row) => (
        <li key={row.id} className="task-row">
          <div className="task-main">
            <span className="task-title">
              {Number(row.percent_complete)}%{' '}
              <span className="tree-code">
                ({Number(row.delta) >= 0 ? '+' : ''}
                {Number(row.delta)})
              </span>
            </span>
            {row.note && <span className="task-description">{row.note}</span>}
            <span className="task-meta">
              <span>{formatDate(row.entry_date)}</span>
              {row.recorded_by_name && <span>{row.recorded_by_name}</span>}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}
